import type { CrossFilterParsingError } from ".";
import { CROSS_FILTER_PARSING_ERROR } from ".";
import t from "../../../../../i18n";
import { FILE_PARSING_ERROR } from "../../../common/file";
import type { ParsingErrorMessageMap } from "../../../types";

/**
 * @note: Quantity names must not have a "-" in order for the error message to be displayed correctly.
 */

/**
 * @description Error messages that can be presented to the user by the quantity parser.
 */
export const CrossFilterParsingErrorMessages: ParsingErrorMessageMap<
  CROSS_FILTER_PARSING_ERROR | FILE_PARSING_ERROR
> = {
  [FILE_PARSING_ERROR.FILE_EMPTY]: () =>
    t("setup.diagnosis.observable.import_observables.error.empty_file"),
  [FILE_PARSING_ERROR.FILE_SIZE_LIMIT_EXCEEDED]: () =>
    t("setup.diagnosis.observable.import_observables.error.file_size_limit_exceeded"),
  [FILE_PARSING_ERROR.FILE_INVALID_TYPE]: () =>
    t("setup.diagnosis.observable.import_quantities.error.invalid_format"),
  [CROSS_FILTER_PARSING_ERROR.FILE_INVALID_CONTENT]: () =>
    t("quantities.parse.error.file_invalid_content"),
  [CROSS_FILTER_PARSING_ERROR.DATA_FILE_EMPTY]: () =>
    t("setup.simulation.input_model.error.data_file_empty"),
  [CROSS_FILTER_PARSING_ERROR.MIN_SUPERIOR_TO_MAX]: errorData =>
    t("setting.parser.quantity.import.error.max_inferior_to_min", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.MAX_STD_NEGATIVE]: errorData =>
    t("setting.parser.quantity.import.error.std_positive", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.INVALID_NUMBER_MINIMUM_VALUE]: errorData =>
    t("setting.parser.quantity.import.error.invalid_number_minimum", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.INVALID_NUMBER_MAXIMUM_VALUE]: errorData =>
    t("setting.parser.quantity.import.error.invalid_number_maximum", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.INVALID_NUMBER_MAX_STD_VALUE]: errorData =>
    t("setting.parser.quantity.import.error.invalid_number_maximum_std", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.VALUE_ALL_NULL]: errorData =>
    t("setting.parser.quantity.import.error.least_one_filter_value_required", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.NOT_FOUND_INVALIDATED_QUANTITY]: errorData =>
    t("setting.parser.quantity.import.error.quantity_not_existing_in_project", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.NOT_FOUND_SOURCE_QUANTITY]: errorData =>
    t("setting.parser.quantity.import.error.quantity_not_existing_in_project", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.DATA_QUANTITY_DUPLICATED]: errorData =>
    t("setting.cross_filter.import.error.data_quantity_duplicated", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
  [CROSS_FILTER_PARSING_ERROR.ONLY_IMPORT_AND_METROLOGY_FILTERS]: () =>
    t("quantities.parse.error.only_import_and_metrology_filters"),
  [CROSS_FILTER_PARSING_ERROR.DATA_INVALIDATED_QUANTITY_EMPTY]: () =>
    t("quantities.parse.error.data_invalidated_quantity_empty"),
  [CROSS_FILTER_PARSING_ERROR.DATA_SOURCE_QUANTITY_EMPTY]: () =>
    t("quantities.parse.error.data_source_quantity_empty"),
  [CROSS_FILTER_PARSING_ERROR.CROSS_FILTER_BY_PASS]: () =>
    t("quantities.parse.error.cross_filter_by_pass"),
  [CROSS_FILTER_PARSING_ERROR.INVALID_SOURCE_QUANTITY]: errorData =>
    t("setting.cross_filter.import.error.invalid_source_quantity", {
      quantityName: errorData.hint?.split("/")[0] || "",
      source: errorData.hint?.split("/")[1] || "",
    }),
};

/**
 * @description Get error message from error kind.
 * @param error CrossFilterParsingError
 * @returns Translated error message.
 */
export const getCrossFilterParsingErrorMessage = (error: CrossFilterParsingError) => {
  const errorMessage = CrossFilterParsingErrorMessages[error.kind];
  return errorMessage ? errorMessage(error.data) : "Unknown error";
};
